import * as React from "react";
import { useState, useEffect, useContext } from "react";
import AddCircle from "@mui/icons-material/AddCircle";
import EraseIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridColumns } from "@mui/x-data-grid";
import { UserContext } from "../../App";
import Notification from "../../components/common/Notification";
import RotinaDataset from "../../components/data/RotinaDataSet";
import RotinaGrupoDataset from "../../components/data/GrupoBroadcastRotinaDataSet";
import { RotinaGrupo } from "../../components/classes/RotinaGrupoBroadcast";
import Rotina from "../../components/classes/Rotina";

import { useTranslation } from "react-i18next";

export default function RotinaGruposModal(props: any) {
  const { t } = useTranslation();

  const { openDialog, setOpenDialog, grupoId } = props;
  const user: any = useContext(UserContext);

  const rotinaDataSet: RotinaDataset = new RotinaDataset();
  const rotinaGrupoDataset: RotinaGrupoDataset = new RotinaGrupoDataset();

  const [rotinasGrupo, setRotinasGrupo] = useState<Rotina[]>([]);
  const [rows, setRows] = useState<RotinaGrupoRow[]>([]);
  const [openAddRotina, setOpenAddRotina] = useState<boolean>(false);
  const [rotinas, setRotinas] = useState<Rotina[]>([]);
  const [rotinasOptions, setRotinasOptions] = useState<AutocompleteOption[]>(
    []
  );
  const [inputRotina, setInputRotina] =
    React.useState<AutocompleteOption | null>(null);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  useEffect(() => {
    if (refresh) {
      fetchRotinasGrupo();
      setRefresh(false);
    }
  }, [refresh]);

  useEffect(() => {
    fetchRotinasGrupo();
  }, [grupoId]);

  useEffect(() => {
    if (rotinasGrupo && rotinasGrupo.length > 0) {
      setRows((): RotinaGrupoRow[] =>
        rotinasGrupo.map((item: Rotina) => {
          return {
            id: item.id,
            rotina: item.nome,
          };
        })
      );
    } else {
      setRows([]);
    }
  }, [rotinasGrupo]);

  useEffect(() => {
    const rotinaCache: string = String(localStorage.getItem("RotinaCache"));
    if (rotinaCache && rotinaCache !== "null" && rotinaCache !== "undefined") {
      const rotinaParse: Rotina[] = JSON.parse(rotinaCache);
      if (rotinaParse) {
        setRotinas(rotinas);
      }
    } else {
      rotinaDataSet.Listar().then((result) => {
        setRotinas(result);
        localStorage.setItem("RotinaCache", JSON.stringify(result));
      });
    }
  }, []);

  const columns: GridColumns = [
    {
      field: "id",
      headerName: "#",
      width: 80,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "rotina",
      headerName: t("common.name"),
      width: 350,
      editable: false,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "deleteico",
      headerName: "",
      width: 50,
      sortable: false,
      renderCell: (cellValues) => {
        return (
          <Tooltip title={t("common.delete")}>
            <span>
              <IconButton
                disabled={user.isSuporte}
                onClick={(event) => {
                  handleClickDelete(event, cellValues);
                }}
              >
                <EraseIcon />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  interface RotinaGrupoRow {
    id: number;
    rotina: string;
  }

  interface AutocompleteOption {
    id: number;
    label: string;
  }

  const handleClickDelete = (_event: any, params: any) => {
    if (params?.id) {
      rotinaGrupoDataset.Desvincular(params.id, grupoId).then((response) => {
        setNotify({
          isOpen: true,
          message: t("pages.emailsGroups.text.routineRemovedWithSuccess"),
          type: "success",
        });
        setRefresh(true);
      });
    }
  };

  const handleClickAdicionarRotinaAoGrupo = () => {
    if (inputRotina) {
      adicionarRotina(inputRotina.id, inputRotina.label);
    }
    setOpenAddRotina(false);
  };

  function fetchRotinasGrupo() {
    if (grupoId) {
      rotinaDataSet.ListarPorGrupo(grupoId).then((result) => {
        setRotinasGrupo(result);
      });
    } else {
      setRotinasGrupo([]);
    }
  }

  function adicionarRotina(id: number, nome: string) {
    rotinaGrupoDataset
      .Adicionar({
        id: 0,
        idRotina: id,
        idGrupoBroadcast: grupoId,
        grupoBroadcast: [],
        rotina: [
          {
            id,
            nome,
            criadoEm: new Date(),
            d_E_L_E_T_E: false,
            excluidoEm: new Date(),
          },
        ],
        criadoEm: new Date(),
        d_E_L_E_T_E: false,
        excluidoEm: new Date(),
        idParceiro: 0,
        parceiro: [],
      })
      .then((response) => {
        setNotify({
          isOpen: true,
          message: t("pages.emailsGroups.text.routineAddedToGroupWithSuccess"),
          type: "success",
        });
        setRefresh(true);
      });
  }

  const handleClickOpenAddRotina = () => {
    setInputRotina(null);
    setRotinasOptions(rotinaToOption(rotinas));
    setOpenAddRotina(true);
  };

  function rotinaToOption(
    rotinasList: Rotina[]
  ): React.SetStateAction<AutocompleteOption[]> {
    if (!rotinasList || rotinasList.length === 0) {
      rotinasList = getRotinasListFromCache();
    }
    return rotinasList
      ? rotinasList
          .filter((f) => {
            const rotinasArr = rotinasGrupo?.map((item) => item.id);
            return !rotinasArr.includes(f.id);
          })
          .map((item) => {
            return {
              id: item.id,
              label: item.nome,
            };
          })
      : [];
  }

  function getRotinasListFromCache(): Rotina[] {
    const rotinaCache: string = String(localStorage.getItem("RotinaCache"));

    if (rotinaCache && rotinaCache !== "null") {
      const rotinaParse: Rotina[] = JSON.parse(rotinaCache);
      if (rotinaParse) {
        return JSON.parse(rotinaCache);
      }
    }
    return [];
  }

  return (
    <div>
      <div>
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          aria-labelledby="alert-dialog-title"
        >
          <DialogTitle id="alert-dialog-title">
            {t("pages.emailsGroups.text.linkedRoutines")}
          </DialogTitle>
          <DialogContent>
            <Box className="container">
              <Box className="containerToolbar">
                <Tooltip title={t("pages.emailsGroups.text.addRoutine")}>
                  <IconButton onClick={handleClickOpenAddRotina}>
                    <AddCircle />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box
                className="gridScroll"
                sx={{ minWidth: "500px", height: "90vh" }}
              >
                <DataGrid
                  rows={rows}
                  columns={columns}
                  rowsPerPageOptions={[5, 10, 25]}
                  autoPageSize
                  disableSelectionOnClick
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOpenDialog(false)}
              autoFocus
            >
              {t("common.close")}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <div>
        <Dialog onClose={() => setOpenAddRotina(false)} open={openAddRotina}>
          <DialogTitle>{t("pages.emailsGroups.text.addRoutine")}</DialogTitle>
          <DialogContent>
            <Autocomplete
              autoComplete
              id="autoCompleteRotinas"
              options={rotinasOptions}
              value={inputRotina}
              onChange={(_event: any, newValue: AutocompleteOption | null) => {
                setInputRotina(newValue);
              }}
              sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("pages.emailsGroups.text.routine")}
                  variant="standard"
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="success"
              autoFocus
              onClick={() => handleClickAdicionarRotinaAoGrupo()}
            >
              {t("common.add")}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOpenAddRotina(false)}
              autoFocus
            >
              {t("common.close")}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
}
